<template>
	<div
		:id="uniktId"
		class="_eui-highcharts"
		:class="{
			'-inaktiverad':skaVaraInaktiverad,
			'-osynlig':skaVaraOsynlig,
		}"
		:disabled="skaVaraInaktiverad"
	>

		<div
			ref="diagramcontainern"
			class="_eui-highcharts_diagramcontainern"
		></div>

	</div>
</template>


<script>
// ##########################################################################################################################

	import { euiLifecycleStatusesMixin } from 'v-mixins/euiLifecycleStatusesMixin';

	// Klon av originalet på https://github.com/Kelin2025/vue-responsive-components:
	import { ResponsiveContainerMixin } from 'ResponsiveContainer';

	import Highcharts from 'HighchartsMedInledandeDelayForTooltips';
	//import * as patternFill from 'highcharts/modules/pattern-fill';
	//patternFill(Highcharts);	// Init!


	// Gemensam för alla sådana här komponenter på en sida, vilket är precis det vi vill:
	let uniktIdCounter = 0;

	Highcharts.setOptions({
		// Språkanpassningar
		lang: {
			contextButtonTitle: 'Nedladdningsmeny',
			decimalPoint: ',',
			downloadCSV: 'Ladda ner CSV-fil med tabelldata',
			downloadJPEG: 'Ladda ner JPEG bildfil',
			downloadPDF: 'Ladda ner PDF-dokument',
			downloadPNG: 'Ladda ner PNG bildfil',
			downloadSVG: 'Ladda ner SVG-fil med vektorgrafik',
			downloadXLS: 'Ladda ner Excelfil med tabelldata',
			drillUpText: 'Tillbaka till {series.name}',
			invalidDate: undefined,
			loading: 'Laddar...',
			noData: 'Inga data att visa',
			numericSymbolMagnitude: 1000,
			numericSymbols: [' tkr', ' mnkr', ' mdkr', 'T', 'P', 'E'],
			resetZoomTitle: 'Återställ inzoomningen 1:1',
			viewData: 'Visa data i tabell',
			printChart: 'Skriv ut diagrammet',
			resetZoom: 'Återställ inzoomningen',
			thousandsSep: ' ',
			shortMonths: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
			months: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'],
			shortWeekdays:  ['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'],
			weekdays: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag']
		}
	});


	// ###########################################################################################################

	export default {
		name: 'eui-highcharts',

		mixins: [
			euiLifecycleStatusesMixin,
			ResponsiveContainerMixin,
		],

		// Det måste finnas minst en breakpoint för att ResponsiveMixin ska skrida till verket och sätta w/h under data:
		breakpoints: { dummy() { return true }, },

		props: {
			vpOptions: {
				type: Object,
				required: true,
			},
		},

		data() {
			return {
				uniktId: 'eui-highcharts-unikt-' + (++uniktIdCounter),
				diagrammet: null,
				options: undefined,	// Sätts i created().

				// Även el:{ } från mixinen ovan!
			}
		},

		computed: {
			elWidth() {
				// Obs! Här används el.* ist.f. $el.clientWidth, för att den, tack vara ResponsiveMixin, övervakas och triggar ändringar av denna computed som kan användas i en watch:
				//return this.$el.clientWidth;	// 'offsetX' = "outer", medan 'clientX' = inner med border och ev rulln.list borträknad.
				return Math.round(this.el.width);	// Heltal!
			},

			elHeight() {
				// Obs! Här används el.* ist.f. $el.clientWidth, för att den, tack vara ResponsiveMixin, övervakas och triggar ändringar av denna computed som kan användas i en watch:
				return Math.round(this.el.height);	// Heltal!
			},

			elDimensions() {
				return (this.elWidth + ' x ' + this.elHeight);
			},

			skaVaraOsynlig() {
				return (this.vpOsynlig || !this.lifecycleStatuses.mixinMountedAndNextTickHasOccured);	//AndSecondNextTickHasOccured);
			},

			skaVaraInaktiverad() {
				return (this.vpInaktiverad);				// || !this.lifecycleStatuses.mixinMountedAndSecondNextTickHasOccured);
			},
		},

		watch: {

			elDimensions(nyttVarde, tidigareVarde) {
				//console.log('Ny dim: ' + tidigareVarde + ' -> ' + nyttVarde);

				// Denna watch triggas *alltid* en inledande gång, när elementet får sin storlek (0x0 –> XxY). Därav att diagrammet kan skapas här:
				// Sen triggas den såklart också av ev efterföljande storleksändringar.

				if (this.elHeight !== 0) {
					// Det omgärdande elementet har nu fått sin storlek:

					//console.log('watch:elDimensions och elHeight !== 0, this.diagrammet = ' + this.diagrammet);

					// Det är en massa strul med att *förändra* befintligt diagram till ett "nytt" diagram (data, options...), så en både enklare och säkrare metod är att helt enkelt kasta och bygga om det från scratch:
					// Ny anpassningskod i skapaDiagrammet *förutsätter* numera också att diagrammet skapas om med den istället för 'this.diagrammet.reflow();' & Co.
					this.skapaDiagrammet();
				}
			},

			vpOptions: {
				// Hm.. Det verkar som om denna triggas en inledande gång, med nyttVarde === tidigareVarde (deep), trots att så väl inte borde ske? Är det så att 'deep' implicit även betyder 'immidiate'???

				// Obs! Denna watch är "deep"!
				handler(nyttVarde, tidigareVarde)
				{
					//console.log('watch:vpOptions, this.diagrammet = ' + this.diagrammet);

					//this.options = nyttVarde;
					// For deep cloning, we need to use alternatives because Object.assign() copies property values. If the source value is a reference to an object, it only copies that reference value.
					//this.options = Object.assign({}, nyttVarde);
					// Deep Clone:
					this.options = JSON.parse(JSON.stringify(nyttVarde));

					// Aktivera event handlers för klick på diagramytan:
					this.options.chart.events.click = this.emitClickEvent;
					this.options.plotOptions.series.events.click = this.emitClickEvent;
					this.options.xAxis.events.click = this.emitClickEvent;
					this.options.yAxis.events.click = this.emitClickEvent;

					// Det är en massa strul med att *förändra* befintligt diagram till ett "nytt" diagram (data, options...), så en både enklare och säkrare metod är att helt enkelt kasta och bygga om det från scratch:
					// Ny anpassningskod i skapaDiagrammet *förutsätter* numera också att diagrammet skapas om med den istället för 'this.diagrammet.reflow();' & Co.
					this.skapaDiagrammet();
				},
				deep: true,
				//immediate: true,		// OBS! Exekveras även initialt!
			},
		},

		methods: {

			skapaDiagrammet()
			{
				// Det är en massa strul med att *förändra* befintligt diagram till ett "nytt" diagram (data, options...), så en både enklare och säkrare metod är att helt enkelt kasta och bygga om det från scratch.
				// Ny anpassningskod i skapaDiagrammet *förutsätter* numera också att diagrammet skapas om med den istället för 'this.diagrammet.reflow();' & Co.

				if (this.diagrammet)
				{
					// Om ett tidigare diagram skapats:
					// Remove the chart and purge memory. This method is called internally before adding a second chart into the same container, as well as on window unload to prevent leaks.
					this.diagrammet.destroy();
				}

				let d = new Highcharts.Chart(this.$refs.diagramcontainern, this.options);

				if (this.options.chart.type === 'line' || this.options.chart.type === 'spline')
				{
					// Om linjediagram:

					// Här läggs under vissa förutsättningar en tjockare linje till, för värdet 0:
					// Måste göras run-time!

					const yAxis = d.yAxis[0];

					if (yAxis)
					{
						const extremer = yAxis.getExtremes();

						if (extremer.min < 0 && extremer.max > 0)
						{
							// Om noll-linjen (gridLines) inte är i botten eller toppen av diagrammet, utan nånstans mitt i:
							// Lägg till en plotLine som förstärker linjen:
							// z-index 2 = mellan gridLines och serier:
							yAxis.addPlotLine({ value: 0, color: '#aaaaaa', width: 1, zIndex: 2 });
						}
					}

					// Här flyttas under vissa förutsättningar labels på x-axeln till *mellan* ticksen istället för vid ticksen:
					// Måste göras run-time!
					this.placeraVidBehovLabelsPaXaxelnMellanTicks(d);
				}

				this.diagrammet = d;
			},

			placeraVidBehovLabelsPaXaxelnMellanTicks(d)
			{
				// Här flyttas under vissa förutsättningar labels på x-axeln till *mellan* ticksen istället för vid ticksen:
				// Måste göras run-time!
				// Observera att i det case som berörs så har endast sista dagen i varje månad ett tick.
				// Todo: Obs! Detta blev en SSO-specifik sak som egentligen inte borde ligga här, då denna component borde vara generisk!

				// Todo: Detta gränsvärde borde DRYas. Finns på fler ställen i ChartServices.cs + även i euiHighcharts.vue:
				const mångaDagar = (this.options.xAxis.categories.length > 32);	//! 31 + IB/Zero!

				if (mångaDagar)
				{
					this.placeraLabelsPaXaxelnMellanTicks(d);
				}
			},

			placeraLabelsPaXaxelnMellanTicks(d)
			{
				// Flytta labels på x-axeln till *mellan* ticksen istället för vid ticksen:
				// Måste göras run-time!

				// Tar höjd för:
				// a) att ticks kanske *inte* visas för varje datapunkts (pga Highcharts automatik eller manuellt satta tickPositions) och
				// b) att diagrammet kan vara zoomat.
				// Distansen som labels ska flyttas beräknas efter de två första visade ticksen. Höjd tas alltså inte för (allt för) oregelbundra ticks.

				const xAxis = d.xAxis[0];

				if (xAxis)
				{
					const ticks = xAxis.ticks;							// Bara de som visas just nu (efter ev inzoomning)!
					const tickPositions = xAxis.tickPositions;	// Bara de som visas just nu (efter ev inzoomning)!

					//if (Object.keys(ticks).length >= 2)
					if (tickPositions.length >= 2)
					{
						// Om minst 2 st ticks är synliga (med aktuell inzoomning):

						// En liten function:
						const getPosition = function(tick) {
							var axis = tick.axis;
							return Highcharts.Tick.prototype.getPosition.call(tick, axis.horiz, tick.pos, axis.tickmarkOffset);
						};

						const tick1x = getPosition(ticks[tickPositions[0]]).x;
						const tick2x = getPosition(ticks[tickPositions[1]]).x;

						const tickPixelInterval = tick2x - tick1x;

						// Beräkna lämplig rotation:
						// Todo: Återigen är denna väldigt SSO-specifik!

						const rotationer = [
							// Övre gräns för tickPixelInterval , rotation
							{ grans: 20, rotation: -75 },
							{ grans: 22, rotation: -65 },
							{ grans: 24, rotation: -55 },
							{ grans: 26, rotation: -45 },
							{ grans: 28, rotation: -35 },
							{ grans: 30, rotation: -25 },
							{ grans: 999999, rotation: 0 }
						];

						// js-variant of linq firstOrDefault():https://gist.github.com/DanDiplo/30528387da41332ff22b:
						const item = rotationer.filter((item) => { return tickPixelInterval < item.grans; })[0] || null;

						// Flytta label X pixels åt vänster:
						xAxis.update(
							{
								labels:
								{
									x: -(tickPixelInterval / 2),
									rotation: item.rotation,
								}
							}
						);
					}
				}
			},

			emitClickEvent(event) {
				// Skicka event att det klickats på diagrammet:
				this.$emit('klickat-pa-highcharts');
			},
		},



		//== Lifecycle hooks  ================================================================================================================

		//beforeCreate() {
		//	// Called synchronously immediately after the instance has been initialized, before data observation and event/watcher setup.
		//},

		created() {
			// Called synchronously after the instance is created. At this stage, the instance has finished processing the options which means the following have been set up: data observation, computed properties, methods, watch/event callbacks. However, the mounting phase has not been started, and the $el property will not be available yet.

			//this.options = this.vpOptions;
			// For deep cloning, we need to use alternatives because Object.assign() copies property values. If the source value is a reference to an object, it only copies that reference value.
			//this.options = Object.assign({}, this.vpOptions);
			// Deep Clone:
			this.options = JSON.parse(JSON.stringify(this.vpOptions));

			// Aktivera event handlers för klick på diagramytan:
			this.options.chart.events.click = this.emitClickEvent;
			this.options.plotOptions.series.events.click = this.emitClickEvent;
			this.options.xAxis.events.click = this.emitClickEvent;
			this.options.yAxis.events.click = this.emitClickEvent;
		},

		beforeDestroy() {
			if (this.diagrammet) {
				// Remove the chart and purge memory. This method is called internally before adding a second chart into the same container, as well as on window unload to prevent leaks.
				this.diagrammet.destroy();
			}
		},
	}
</script>
